import "./menu.js";
import { $, $$ } from "./utils.js";

import Splide from "@splidejs/splide";
import splideStyles from "@splidejs/splide/dist/css/splide.min.css";

// 先頭スライドのメイン画像（.home-hero-thumb）が読めたら .home-hero をフェードイン（他スライドは遅延読み込み可）
function initHomeHeroFadeIn() {
  const hero = document.querySelector(".home-hero");
  if (!hero) return;

  const firstThumb = hero.querySelector(".home-hero-item .home-hero-thumb");
  if (!firstThumb) {
    hero.classList.add("is-loaded");
    return;
  }

  const waitImage = (img) =>
    new Promise((resolve) => {
      if (img.complete) {
        resolve();
        return;
      }
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    });

  waitImage(firstThumb).then(() => {
    hero.classList.add("is-loaded");
  });
}

window.addEventListener("DOMContentLoaded", initHomeHeroFadeIn);

// home-hero（Splide）
const HERO_AUTO_ADVANCE_MS = 4 * 1000;
const HERO_SLIDE_DURATION_MS = 2000;
const HERO_MOBILE_BREAKPOINT = 1023;

function injectSplideCss() {
  // esbuild の loader 設定により css を文字列として読み込むため、styleとして注入します
  if (document.getElementById("splide-css")) return;
  const style = document.createElement("style");
  style.id = "splide-css";
  style.textContent = splideStyles;
  document.head.appendChild(style);
}

function installHomeHeroTransitionTouchGuard(hero, splide) {
  const track = hero.querySelector(".splide__track");
  if (!track) return;

  const mobileQuery = window.matchMedia(`(max-width: ${HERO_MOBILE_BREAKPOINT}px)`);
  let isTransitioning = false;
  let transitionTimer = 0;

  const endTransition = () => {
    isTransitioning = false;
    if (transitionTimer) {
      window.clearTimeout(transitionTimer);
      transitionTimer = 0;
    }
  };

  const startTransition = () => {
    isTransitioning = true;
    if (transitionTimer) window.clearTimeout(transitionTimer);
    transitionTimer = window.setTimeout(endTransition, HERO_SLIDE_DURATION_MS + 100);
  };

  const stopSplideTouchWhileMoving = (event) => {
    if (!mobileQuery.matches || !isTransitioning) return;
    event.stopImmediatePropagation();
  };

  ["touchstart", "touchmove", "pointerdown", "pointermove"].forEach((eventName) => {
    track.addEventListener(eventName, stopSplideTouchWhileMoving, {
      capture: true,
      passive: true,
    });
  });

  splide.on("move", startTransition);
  splide.on("moved", endTransition);
  splide.on("destroy", endTransition);
}

function initHomeHeroSplide() {
  const hero = document.querySelector(".home-hero");
  const dots = document.querySelectorAll(".home-hero-pagination-dot");
  if (!hero || !dots.length) return;
  if (hero.dataset.splideInited) return;
  hero.dataset.splideInited = "true";

  injectSplideCss();

  const slideCount = hero.querySelectorAll(".home-hero-item").length;
  const updateActiveDot = (index) => {
    dots.forEach((dot, i) => {
      const active = i === index;
      dot.setAttribute("aria-selected", active);
      dot.classList.toggle("is-active", active);
    });
  };

  dots.forEach((dot) => {
    dot.addEventListener("click", () => {
      const index = Number(dot.dataset.index);
      if (!Number.isFinite(index)) return;
      const safeIndex = slideCount ? index % slideCount : index;
      splide.go(safeIndex);
    });
  });

  const splide = new Splide(hero, {
    type: "loop",
    perPage: 1,
    perMove: 1,
    gap: 0,
    arrows: false,
    pagination: false,
    autoplay: true,
    interval: HERO_AUTO_ADVANCE_MS,
    speed: HERO_SLIDE_DURATION_MS,
    waitForTransition: false,
    drag: true,
    // テキスト・ボタン上では横ドラッグを無効化し、縦スクロールを優先
    noDrag: ".home-hero-content, .home-hero-pagination, a, button",
    dragMinThreshold: {
      mouse: 0,
      touch: 10,
    },
    breakpoints: {
      [HERO_MOBILE_BREAKPOINT]: {
        // 縦スクロールと横スワイプの誤判定を減らす（Splide の軸判定と併用）
        dragMinThreshold: {
          mouse: 0,
          touch: 48,
        },
      },
    },
    pauseOnHover: false,
    pauseOnFocus: false,
    focus: "center",
    easing: "cubic-bezier(0.65, 0, 0.35, 1)",
  });

  installHomeHeroTransitionTouchGuard(hero, splide);
  splide.mount();
  updateActiveDot(splide.index);
  splide.on("moved", () => updateActiveDot(splide.index));
}

window.addEventListener("DOMContentLoaded", initHomeHeroSplide);

// 施設案内 .facility-slider（1ページに複数。フェード・ページネーションのみ）
const FACILITY_FADE_MS = 800;
const FACILITY_AUTO_ADVANCE_MS = 3 * 1000;

function initFacilitySliders() {
  const sliders = document.querySelectorAll(".facility-slider");
  if (!sliders.length) return;

  injectSplideCss();

  sliders.forEach((root) => {
    if (root.dataset.splideInited) return;
    root.dataset.splideInited = "true";

    new Splide(root, {
      type: "fade",
      rewind: true,
      perPage: 1,
      gap: 0,
      arrows: false,
      pagination: true,
      speed: FACILITY_FADE_MS,
      autoplay: true,
      interval: FACILITY_AUTO_ADVANCE_MS,
      easing: "cubic-bezier(0.65, 0, 0.35, 1)",
    }).mount();
  });
}

window.addEventListener("DOMContentLoaded", initFacilitySliders);

// ブロックエディタ用 画像スライダー（ACF Block）
function initBlockImageSliders() {
  const sliders = document.querySelectorAll(".block-image-slider");
  if (!sliders.length) return;

  injectSplideCss();

  sliders.forEach((root) => {
    if (root.dataset.splideInited) return;
    root.dataset.splideInited = "true";

    const slideCount = root.querySelectorAll(".splide__slide").length;
    new Splide(root, {
      type: "fade",
      rewind: true,
      perPage: 1,
      arrows: false,
      pagination: slideCount > 1,
      speed: FACILITY_FADE_MS,
      autoplay: slideCount > 1,
      interval: FACILITY_AUTO_ADVANCE_MS,
      easing: "cubic-bezier(0.65, 0, 0.35, 1)",
    }).mount();
  });
}

window.addEventListener("DOMContentLoaded", initBlockImageSliders);

// スクロール量に応じてbodyにクラスを付与
// is-scrolled: ブラウザの高さ分スクロールした時
// is-scrolled-home: ブラウザの高さ × .home-hero-itemの数 分スクロールした時
const body = document.body;

const updateScrollClass = () => {
  const viewportHeight = window.innerHeight;
  const heroCount = document.querySelectorAll(".home-hero-item").length;
  const homeThreshold = viewportHeight * (heroCount || 1);

  if (window.scrollY > viewportHeight) {
    body.classList.add("is-scrolled");
  } else {
    body.classList.remove("is-scrolled");
  }

  if (window.scrollY > homeThreshold) {
    body.classList.add("is-scrolled-home");
  } else {
    body.classList.remove("is-scrolled-home");
  }
};

window.addEventListener("scroll", updateScrollClass, { passive: true });
window.addEventListener("DOMContentLoaded", updateScrollClass);

// 年月プルダウン（div カスタムドロップダウン）
function initYearMonthDropdowns() {
  $$(".js-yearmonth-dropdown").forEach((wrap) => {
    const form = wrap.closest("form");
    const trigger = wrap.querySelector(".js-yearmonth-trigger");
    const panel = wrap.querySelector(".yearmonth-dropdown-panel");
    const options = wrap.querySelectorAll(".yearmonth-option");
    if (!form || !trigger || !panel) return;

    const open = () => {
      panel.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
    };
    const close = () => {
      panel.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
    };

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      if (panel.hidden) open();
      else close();
    });

    options.forEach((opt) => {
      opt.addEventListener("click", (e) => {
        e.preventDefault();
        const val = opt.getAttribute("data-value");
        if (val == null) return;
        const parts = val.split("-");
        const yearInput = form.querySelector('input[name="year"]');
        const monthInput = form.querySelector('input[name="month"]');
        if (yearInput) yearInput.value = parts[0] || "0";
        if (monthInput) monthInput.value = parts[1] || "0";
        const labelEl = wrap.querySelector(".yearmonth-trigger-label");
        if (labelEl) labelEl.textContent = opt.textContent.trim();
        close();
        form.submit();
      });
    });

    document.addEventListener("click", (e) => {
      if (!panel.hidden && !wrap.contains(e.target)) close();
    });
  });
}

window.addEventListener("DOMContentLoaded", initYearMonthDropdowns);

// アンカーリンクのスムーススクロール
window.addEventListener("DOMContentLoaded", () => {
  $$('a[href^="#"]').forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (href === "#") return;
      const target = $(href);
      if (target) {
        e.preventDefault();
        const easing = (t) =>
          t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        const start = window.pageYOffset;
        const end = target.getBoundingClientRect().top + start;
        const duration = 400;
        const startTime = performance.now();

        function animate(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          window.scrollTo(0, start + (end - start) * easing(progress));
          if (progress < 1) requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
      }
    });
  });
});
